import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d52e88ibfdatfusc3tdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Montserrat']">
            Let's <span className="text-[#fcd34d]">Connect</span>
          </h2>
          <p className="text-white/60 text-lg font-['Roboto'] max-w-2xl mx-auto">
            Let's craft something extraordinary together
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
            <div>
              <label htmlFor="name" className="block text-white/80 font-['Roboto'] mb-2">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#fcd34d]/50 focus:shadow-lg focus:shadow-[#fcd34d]/10 transition-all duration-300 font-['Roboto'] text-sm"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-white/80 font-['Roboto'] mb-2">
                Your Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#fcd34d]/50 focus:shadow-lg focus:shadow-[#fcd34d]/10 transition-all duration-300 font-['Roboto'] text-sm"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-white/80 font-['Roboto'] mb-2">
                Your Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                maxLength={500}
                rows={6}
                className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#fcd34d]/50 focus:shadow-lg focus:shadow-[#fcd34d]/10 transition-all duration-300 font-['Roboto'] resize-none text-sm"
                placeholder="Tell me about your project..."
              ></textarea>
              <p className="text-white/40 text-sm mt-2 font-['Roboto']">
                {formData.message.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-[#fcd34d] to-[#facc15] text-[#0f172a] rounded-xl font-semibold font-['Poppins'] text-lg hover:shadow-2xl hover:shadow-[#fcd34d]/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-xl text-green-300 text-center font-['Roboto']">
                Message sent successfully! I'll get back to you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-center font-['Roboto']">
                Something went wrong. Please try again or contact me directly.
              </div>
            )}
          </form>

          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex flex-wrap justify-center gap-6">
              <a
                href="tel:+923440674672"
                className="flex items-center gap-3 text-white/70 hover:text-[#fcd34d] transition-colors duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#fcd34d]/10 rounded-full">
                  <i className="ri-phone-line text-xl text-[#fcd34d]"></i>
                </div>
                <span className="font-['Roboto']">+92 3440674672</span>
              </a>

              <a
                href="mailto:syedabdulraheem874@gmail.com"
                className="flex items-center gap-3 text-white/70 hover:text-[#fcd34d] transition-colors duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#fcd34d]/10 rounded-full">
                  <i className="ri-mail-line text-xl text-[#fcd34d]"></i>
                </div>
                <span className="font-['Roboto']">Email Me</span>
              </a>

              <a
                href="https://www.instagram.com/gfxwithrahim/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/70 hover:text-[#fcd34d] transition-colors duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#fcd34d]/10 rounded-full">
                  <i className="ri-instagram-line text-xl text-[#fcd34d]"></i>
                </div>
                <span className="font-['Roboto']">Instagram</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <a
        href="https://wa.me/923440674672"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 flex items-center justify-center bg-[#25D366] rounded-full shadow-2xl shadow-[#25D366]/50 hover:scale-110 transition-all duration-300 z-50 cursor-pointer animate-bounce"
      >
        <i className="ri-whatsapp-line text-3xl text-white"></i>
      </a>

      <footer className="mt-24 pt-12 border-t border-white/10 text-center">
        <p className="text-white/60 font-['Roboto'] mb-4">
          © 2025 Syed Abdul Raheem Designs. All rights reserved.
        </p>
        <a
          href="https://readdy.ai/?ref=logo"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/40 hover:text-[#fcd34d] transition-colors duration-300 font-['Roboto'] text-sm cursor-pointer"
        >
          Powered by Readdy
        </a>
      </footer>
    </section>
  );
}
